<template>
	<div class="product-images-wrapper">
		<image-modal
			v-if="showZoomModal && product.hasImages"
			:images="imageUrls"
			:index="index"
			:start-index="images?.indexOf(activeImage) >= 0 ? images.indexOf(activeImage) : 0"
			@cancel="closeZoomModal"
		/>
		<div v-if="product.imageCaption" class="block text-center image-caption">
			*{{ product.imageCaption }}
		</div>
		<!-- if it's not mobile, render zoom component with image list -->
		<div v-if="videoShowing" class="pdp-video">
			<div>
				<iframe
					class="absolute"
					:src="product.video.url"
					frameborder="0"
					enablejsapi="true"
					allowfullscreen
				></iframe>
			</div>
		</div>
		<div class="active-image-wrapper">
			<div
				v-if="!videoShowing"
				class="active-image"
				@click="openZoomModal"
				@keydown.enter="openZoomModal"
				tabindex="0"
				type="image"
				:aria-label="`Product Image ${product.name} Enlarged Image, zoom in`"
			>
				<div class="icon-zoom"></div>
				<picture>
					<source v-if="activeImage.fullWebp" :srcset="activeImage.fullWebp" type="image/webp" />
					<source :srcset="activeImage.full" type="image/jpeg" />
					<img :src="activeImage.full" :alt="product.name" />
				</picture>
			</div>
		</div>
		<div class="image-thumbs-wrapper">
			<div class="image-thumbs" v-if="images.length > 1 || !!product.video">
				<button
					v-for="(img, i) in limitedImages"
					:key="i"
					@click="selectImg(img)"
					:class="['image-thumb', {'active': img.full == activeImage?.full }]"
					:aria-label="`Show product Image ${product.name} ${i+1} out of ${images.length}, ${img.full === activeImage?.full ? 'selected' : 'not selected'}`"
				>
					<img :src="imageThumbs ? imageThumbs[i] : ''" alt="" width="73" height="73" />
				</button>
				<button
					v-if="product.video"
					:class="['image-thumb', {'active': videoShowing }]"
					aria-label="Show informational video about product"
					@click="showVideo"
				>
					<div class="image-thumb-overlay"></div>
					<i class="fa fa-solid fa-play fa-2x image-thumb-icon"></i>
					<img :src="product.video.thumbnailUrl" :alt="product.name" width="73" height="73" />
				</button>
				<button
					v-if="hasTooManyImagesForThumbnails && !!imageThumbs"
					@click="openZoomModal"
					class="image-thumb show-more"
					:aria-label="`Show ${remainingThumbnails} more product images`"
					:style="`background-image: url(${imageThumbs[maxThumbnailsToShow]})`"
				>
					<span class="show-more-text">+{{ remainingThumbnails }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { checkWebpSupport } from '../../helpers/functions.js';

export default {
	name: "ProductImages",
	components: {
		ImageModal: () => import(/* webpackChunkName: "imagemodal" */ './ImageModal.vue'),
	},
	computed: {
		product() {
			return this.$store.state.product;
		},
		images() {
			return this.$store.state.activeImages;
		},
		activeImage() {
			if (this.image !== null) {
				return this.image;
			}
			if (this.videoShowing) {
				return null;
			}
			return this.images[0];
		},
		imageUrls() {
			if (!this.images.length || this.supportsWebp === null) {
				return [];
			}

			return this.images.map(image => this.supportsWebp ? image.fullWebp : image.full);
		},
		imageThumbs() {
			if ((!this.images.length) || (this.supportsWebp === null)) {
				return null;
			}

			return this.images.map(image => this.supportsWebp ? image.thumbWebp : image.thumb);
		},
		maxThumbnailsToShow() {
			const hasVideo = !!this.product && !!this.product.video;
			const toShow = {
				mobile: 3,
				tablet: 4,
				desktop: 6,
			}[this.$mq];

			return hasVideo ? toShow - 1 : toShow;
		},
		remainingThumbnails() {
			return this.images.length - (this.maxThumbnailsToShow - 1);
		},
		hasTooManyImagesForThumbnails() {
			return this.images.length > this.maxThumbnailsToShow;
		},
		limitedImages() {
			return this.hasTooManyImagesForThumbnails
				? this.images.slice(0, this.maxThumbnailsToShow - 1)
				: this.images;
		},
		isMobile() {
			return this.$mq === 'pdp-mobile';
		}
	},
	data() {
		return {
			forceUnzoom: false,
			// eslint-disable-next-line vue/no-computed-properties-in-data
			image: this.images ? this.images[0] : null,
			index: 0,
			renderImageList: null,
			showZoomModal: false,
			videoShowing: false,
			supportsWebp: null,
		};
	},
	methods: {
		selectImg(img) {
			this.videoShowing = false;
			this.image = img;
		},
		openZoomModal(event) {
			this.showZoomModal = true;
		},
		closeZoomModal() {
			this.index = null;
			this.showZoomModal = false;
			this.forceUnzoom = false;
		},
		showVideo() {
			this.videoShowing = true;
			this.image = null;
		}
	},
	watch: {
		images(newImages) {
			this.image = newImages[0];
		}
	},
	mounted() {
		checkWebpSupport(isSupported => {
			this.supportsWebp = isSupported;
		});
	},
};
</script>

<style lang="less">
@import '../../../less/_mixins.less';
.product-images-wrapper {
	.pdp-video {
		margin: 0 auto;
		max-width: 600px;
		.aspect-ratio(1,1);
		width: 100%;
		iframe {
			position: absolute;
			height: 100%;
			width: 100%;
			top: 0; right: 0; bottom: 0; left: 0;
		}
	}

	.image-thumbs {
		display: flex;
		margin-top: 15px;
		overflow-x: auto;
		padding-bottom: 5px;
		// Centers thumbnails while enabling overflow-x: scroll to work properly (it doesn't with justify-content: center)
		&::before, &::after {
			content: '';
			margin: auto;
		}
	}

	.image-thumb {
		position: relative;
		border: 1px solid #D9D9D9;
		border-radius: 6px;
		overflow: hidden;
		cursor: pointer;
		display: inline-flex;
		flex-shrink: 0;
		flex-basis: 75px;
		height: 75px;
		flex-grow: 0;
		margin-left: 15px;
		padding: 0;
		&.active {
			border: 2px solid @BB;
		}
		img {
			width: 100%;
		}
	}

	.image-thumb:first-child {
		margin-left: 0;
		padding: 0;
	}

	.image-thumb-icon {
		color:white;
		position: absolute;
		top:30%;
		left:0;
		width:100%;
		height:100%;
	}

	.image-thumb-overlay {
		position: absolute;
		background-color: black;
		opacity: 50%;
		top:0;
		left:0;
		width:100%;
		height:100%;
	}

	.show-more {
		background-size: cover;
	}

	.show-more-text {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
		color: white;
		font-size: 23px;
		font-weight: 500;
		background-color: rgba(76, 82, 113, 0.8);
		transition: background-color 0.1s ease-out;

		&:hover {
			background-color: rgba(76, 82, 113, 1);
		}
	}

	.carousel{
		width: 95vw;
		.slider-image {
			width: 100%;
		}
	}
	.zoom-hint {
		font-size: 16px;
		text-align: center;
		color: #9E9E9E;
		margin: 10px auto;
	}

	.image-caption {
		.mobile-styles({
			margin: 10px 0 5px 0;
		})
	}

	// Styles from ImageModal, here for testing purposes. Eventually needs to be moved to the component (ImageModal)
	.image-zoom-modal {
		background-color: rgba(0, 0, 0, 0.6);
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		text-align: center;
		padding: 0 15px;
		z-index: 9999;
		overflow-y: auto;
		&.izm-touch {
			background-color: #FFF;
			padding: 5px 0 0;
			.izm-content {
				border-radius: 0;
				padding: 0;
				margin: 0;
				width: 100%;
				height: 100%;
				.izm-close {
					background-color: transparent;
					color: @blue;
					padding: 0;
					top: 0px;
					right: 2px;
					i.fa {
						font-size: 32px;
					}
				}
				.izm-image {
					width: 100%;
				}
				.izm-thumbs {
					background-color: #FFF;
					padding: 10px 0;
					position: relative;
					max-width: 100%;
					width: 100%;
				}
			}
		}
	}
	.izm-content {
		background-color: #fff;
		border-radius: 12px;
		display: inline-block;
		margin: 20px auto;
		max-width: 100%;
		padding: 15px 50px;
		position: relative;
	}
	.izm-text {
		font-size: 18px;
		color: #444;
		margin-bottom: 10px;
		text-align: center;
	}
	.izm-image {
		margin-bottom: 20px;
		width: 78vh;
		max-width: 100%;
	}
	.izm-zoom {
		margin: 0 auto;
	}
	.izm-pinch-zoom {
		height: 75vh;
	}
	.pinch-img {
		height: auto;
		width: 100%;
	}
	.izm-close {
		background-color: @blue;
		border-radius: 50%;
		color: #fff;
		cursor: pointer;
		height: 38px;
		width: 38px;
		position: absolute;
		top: -10px;
		right: -10px;
		i.fa {
			font-size: 24px;
			position: relative;
			top: -1px;
		}
		.mobile-styles(
			{background-color: transparent; color: @blue; padding: 0; top: 0px; right: 2px;
				i.fa {font-size: 32px;}}
		);
	}
	.izm-thumbs {
		margin: 0 auto;
		max-width: 78vh;
		overflow: hidden;

		ul {
			white-space: nowrap;
			overflow-y: scroll;
			overflow-x: visible;
		}

		li {
			display: inline-flex;
			white-space: normal;
			width: 14%;
			margin-right: 15px;
		}
	}
	.izm-thumbs-static {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		margin-bottom: 20px;
		z-index: 9999;
		.izm-thumb {
			margin: 0 20px 0 0;
		}
	}
	.izm-thumb {
		border: solid 1px #afafaf;
		border-radius: 6px;
		cursor: pointer;
		margin: 0 auto;
		overflow: hidden;
		width: 80px;
		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
		&.selected {
			border: solid 2px @blue-main;
		}
	}
	.modal-enter {
		opacity: 0;
	}
	.modal-leave-active {
		opacity: 0;
	}
}
</style>